import { Music, Facebook, Instagram, Youtube, Mail, MessageCircle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/contexts/LanguageContext';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const footerLinks = {
    navigation: [
      { label: t.nav.home, id: 'home' },
      { label: t.nav.about, id: 'about' },
      { label: t.nav.services, id: 'services' },
      { label: t.nav.gallery, id: 'gallery' },
      { label: t.nav.videos, id: 'videos' },
      { label: t.nav.contact, id: 'contact' },
    ],
    services: t.footer.services,
  };

  const socialLinks = [
    { icon: WhatsAppIcon, label: 'WhatsApp', url: 'https://wa.me/491749525375', color: 'hover:bg-[#25D366] hover:text-white' },
    { icon: Instagram, label: 'Instagram', url: 'https://www.instagram.com/quetschnmanfred/', color: 'hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#E1306C] hover:to-[#FD1D1D] hover:text-white' },
    { icon: Mail, label: 'Email', url: 'mailto:quetschnmanfred@gmail.com', color: 'hover:bg-primary hover:text-white' },
  ];

  return (
    <footer className="bg-card border-t-2 border-border">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand Section */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 group">
                <div className="bg-primary text-white p-2 rounded-lg group-hover:scale-110 transition-transform shadow-lg">
                  <Music className="h-6 w-6" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-serif font-bold text-primary">
                    Quetschnmanfred
                  </span>
                  <span className="text-sm text-muted-foreground -mt-1">Manfred Hahnel</span>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {t.footer.tagline}
              </p>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.url}
                      aria-label={social.label}
                      className={`bg-muted/50 p-3 rounded-full transition-all hover:scale-110 shadow-md ${social.color}`}
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-serif font-bold mb-4">{t.footer.quickLinks}</h3>
              <ul className="space-y-3">
                {footerLinks.navigation.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-muted-foreground hover:text-primary transition-colors text-left"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-serif font-bold mb-4">{t.footer.ourServices}</h3>
              <ul className="space-y-3">
                {footerLinks.services.map((service, index) => (
                  <li key={index} className="text-muted-foreground">
                    {service}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-serif font-bold mb-4">{t.footer.contactUs}</h3>
              <div className="space-y-3 text-muted-foreground">
                <p>Munich, Bavaria</p>
                <p>Germany</p>
                <p className="pt-2">
                  <a
                    href="mailto:quetschnmanfred@gmail.com"
                    className="hover:text-primary transition-colors"
                  >
                    quetschnmanfred@gmail.com
                  </a>
                </p>
                <p>
                  <a
                    href="https://wa.me/491749525375"
                    className="inline-flex items-center gap-2 text-[#25D366] hover:underline font-semibold"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    +49 174 9525 375
                  </a>
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-muted-foreground text-sm">
              <span>© {currentYear} Quetschnmanfred - Manfred Hahnel. {t.footer.rights}</span>
              <span className="hidden md:inline">•</span>
              <div className="flex items-center gap-3">
                <a href="/privacy" className="hover:text-primary transition-colors">
                  Datenschutz / Privacy
                </a>
                <span>•</span>
                <a href="/impressum" className="hover:text-primary transition-colors">
                  Impressum
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <span>{t.footer.vibed}</span>
              <a
                href="https://shakespeare.diy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-accent transition-colors font-semibold"
              >
                Shakespeare
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
